import { motion } from "framer-motion";
import { Calendar, Trophy, Gift, Dice5, XCircle, Heart } from "lucide-react";
import { useI18n } from "@/hooks/use-i18n";
import type { TranslationKey } from "@/lib/i18n";
import { captureEvent } from "@/lib/posthog";

export type PresetId =
  | "edition_end"
  | "challenge_end"
  | "guaranteed_prizes"
  | "lottery"
  | "no_prizes"
  | "charity_only";

interface PresetConfig {
  id: PresetId;
  titleKey: TranslationKey;
  descKey: TranslationKey;
  icon: typeof Calendar;
  color: string;
  selectedColor: string;
}

const PRESETS: PresetConfig[] = [
  {
    id: "edition_end",
    titleKey: "presetEditionEnd",
    descKey: "presetEditionEndDesc",
    icon: Calendar,
    color: "text-blue-500",
    selectedColor: "border-blue-500 bg-blue-50 dark:bg-blue-950/30",
  },
  {
    id: "challenge_end",
    titleKey: "presetChallengeEnd",
    descKey: "presetChallengeEndDesc",
    icon: Trophy,
    color: "text-amber-500",
    selectedColor: "border-amber-500 bg-amber-50 dark:bg-amber-950/30",
  },
  {
    id: "guaranteed_prizes",
    titleKey: "presetGuaranteedPrizes",
    descKey: "presetGuaranteedPrizesDesc",
    icon: Gift,
    color: "text-emerald-500",
    selectedColor: "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30",
  },
  {
    id: "lottery",
    titleKey: "presetLottery",
    descKey: "presetLotteryDesc",
    icon: Dice5,
    color: "text-purple-500",
    selectedColor: "border-purple-500 bg-purple-50 dark:bg-purple-950/30",
  },
  {
    id: "no_prizes",
    titleKey: "presetNoPrizes",
    descKey: "presetNoPrizesDesc",
    icon: XCircle,
    color: "text-slate-400",
    selectedColor: "border-slate-400 bg-slate-50 dark:bg-slate-800/30",
  },
  {
    id: "charity_only",
    titleKey: "presetCharityOnly",
    descKey: "presetCharityOnlyDesc",
    icon: Heart,
    color: "text-rose-500",
    selectedColor: "border-rose-500 bg-rose-50 dark:bg-rose-950/30",
  },
];

interface PresetSelectorProps {
  selectedPresets: PresetId[];
  onTogglePreset: (id: PresetId) => void;
  disabled?: boolean;
}

export function PresetSelector({
  selectedPresets,
  onTogglePreset,
  disabled,
}: PresetSelectorProps) {
  const { t } = useI18n();

  return (
    <div className="w-full max-w-md px-2" data-testid="preset-selector">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 text-center">
        {t("selectSections")}
      </h4>
      <p className="text-[10px] text-muted-foreground/70 mb-3 text-center">
        {t("selectSectionsHint")}
      </p>
      <div className="grid grid-cols-2 gap-2">
        {PRESETS.map((preset, idx) => {
          const isSelected = (selectedPresets || []).includes(preset.id);
          const Icon = preset.icon;

          return (
            <motion.button
              key={preset.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05, duration: 0.2 }}
              onClick={() => {
                const willBeSelected = !(selectedPresets || []).includes(preset.id);
                captureEvent("preset_toggled", {
                  preset_id: preset.id,
                  action: willBeSelected ? "selected" : "deselected",
                });
                onTogglePreset(preset.id);
              }}
              disabled={disabled}
              className={`
                relative flex flex-col items-start gap-1.5 p-3 rounded-xl border-2 transition-all duration-200 text-left
                ${
                  isSelected
                    ? `${preset.selectedColor} border-2`
                    : "border-border bg-card hover-elevate"
                }
                ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
              `}
              data-testid={`button-preset-${preset.id}`}
            >
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-2.5 h-2.5"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </motion.div>
              )}
              <Icon className={`w-5 h-5 ${preset.color} shrink-0`} />
              <div className="min-w-0">
                <p className="text-xs font-semibold leading-tight truncate">
                  {t(preset.titleKey)}
                </p>
                <p className="text-[10px] text-muted-foreground leading-snug mt-0.5 line-clamp-2">
                  {t(preset.descKey)}
                </p>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

export { PRESETS };
